import React, { useEffect, useRef, useState } from "react";

type CountUpProps = {
  // The full display value, e.g. "25", "70%", "~14 MSEK". The leading
  // non-digits and trailing characters are kept fixed while the number counts.
  value: string;
  durationMs?: number;
};

// Split into prefix · number · suffix, e.g. "~14 MSEK" -> ["~", "14", " MSEK"].
const NUMBER_RE = /^(\D*)([\d.,]+)(.*)$/s;

function CountUp({ value, durationMs = 1400 }: CountUpProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const parts = value.match(NUMBER_RE);
  const [display, setDisplay] = useState(() =>
    parts ? `${parts[1]}0${parts[3]}` : value,
  );

  useEffect(() => {
    const node = ref.current;
    if (!parts || !node) {
      setDisplay(value);
      return undefined;
    }

    const prefix = parts[1];
    const suffix = parts[3];
    const target = parseFloat(parts[2].replace(/,/g, ""));

    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) {
      setDisplay(value);
      return undefined;
    }

    let raf = 0;
    let startTs = 0;

    const tick = (ts: number) => {
      if (!startTs) startTs = ts;
      const t = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      if (t < 1) {
        setDisplay(`${prefix}${Math.round(eased * target)}${suffix}`);
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(value); // land exactly on the authored value
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          raf = requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, durationMs]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span ref={ref} className="count-up">
      {display}
    </span>
  );
}

export default CountUp;
